/**
 * La Coochie variant picker (blocks/lc-variant-picker.liquid).
 *
 * Selection model: nothing chosen on load (placeholder radio value "0"); colour alone updates price + gallery via
 * a Section Rendering fetch with a partial `option_values` list (Shopify resolves the first available variant of
 * that colour); colour + size resolves the variant and writes `?variant=`. The fetch is published as Horizon's
 * ProductSelectEvent so product-form-component (variant id input, add-to-cart queue), media-gallery and
 * product-price react natively; `resource` is null unless the selection is complete, which makes product-form
 * blank `input[name=id]`. The La Coochie buy button listens to the sync `lc:selection` event.
 *
 * Gallery order (Caroline 2026-09-23): Horizon's product-media-gallery-content renders the selected variant's media
 * as slide 0 and the rest in product order, and media-gallery.js swaps the whole <media-gallery> for the fetched one on
 * every selection (slide 0 = the new colour's image jumps to the front). The picker restores the product's media order
 * (`data-media-order`, the admin order) on the fetched markup before Horizon inserts it, and once on the live gallery at
 * load, then scrolls to the selected image instead of moving it. Thumbnails, the zoom dialog's lists and the grid list
 * are index-coupled with the slides, so they get the same permutation and their `on:click="/…/N"` indexes are renumbered.
 */
import { Component } from '@theme/component';
import { ProductSelectEvent } from '@shopify/events';
import { yieldToMainThread, scheduler } from '@theme/utilities';

/** Attributes that belong to a slot (position in the list), not to the item sitting in it. */
const SLOT_ATTRS = {
  slide: ['aria-hidden', 'style', 'data-view-transition-type'],
  thumbnail: ['aria-label', 'aria-selected'],
  zoomThumbnail: ['aria-selected'],
  zoomMedia: [],
  gridItem: ['data-view-transition-type'],
};
const HANDLER_ATTRS = ['on:click', 'on:pointerenter'];

/** @param {Element} slide */
const mediaIdOf = (slide) => /** @type {HTMLElement | null} */ (slide.querySelector('.product-media[data-media-id]'))?.dataset.mediaId ?? '';

/**
 * Permutation (new position → old index) that sorts `slides` by the product's media order; unknown ids keep their
 * relative order at the end.
 * @param {Element[]} slides
 * @param {string[]} order
 */
function permutationFor(slides, order) {
  const rank = new Map(order.map((id, i) => [id, i]));
  const rankOf = (i) => rank.get(mediaIdOf(slides[i])) ?? Number.MAX_SAFE_INTEGER;
  return slides.map((_, i) => i).sort((a, b) => rankOf(a) - rankOf(b) || a - b);
}

/**
 * Handler indexes Horizon writes as `/…/N` (thumbnail `/select/N`, slide `#zoom-dialog-x/open/N`, zoom `/handleThumbnailClick/N`,
 * grid `/zoom/N`) point at a list position; rewrite the moved item's own from → to.
 * @param {Element} item
 * @param {number} from
 * @param {number} to
 */
function renumberHandlers(item, from, to) {
  const suffix = new RegExp(`/${from}$`);
  for (const el of [item, ...item.querySelectorAll('[on\\:click], [on\\:pointerenter]')]) {
    for (const name of HANDLER_ATTRS) {
      const value = el.getAttribute(name);
      if (value && suffix.test(value)) el.setAttribute(name, value.replace(suffix, `/${to}`));
    }
  }
}

/**
 * Reorders sibling `items` by `perm`; slot attributes stay with the position, handler indexes follow the item.
 * @param {Element[]} items
 * @param {number[]} perm
 * @param {string[]} slotAttrs
 */
function applyPermutation(items, perm, slotAttrs) {
  const parent = items[0]?.parentNode;
  if (!parent || items.length !== perm.length || items.some((el) => el.parentNode !== parent)) return;
  const slots = items.map((el) => slotAttrs.map((name) => el.getAttribute(name)));
  const anchor = items[items.length - 1].nextSibling;
  perm.forEach((oldIndex, newIndex) => {
    const el = items[oldIndex];
    parent.insertBefore(el, anchor);
    slotAttrs.forEach((name, k) => {
      const value = slots[newIndex][k];
      value == null ? el.removeAttribute(name) : el.setAttribute(name, value);
    });
    if (oldIndex !== newIndex) renumberHandlers(el, oldIndex, newIndex);
  });
}

/**
 * Puts a <media-gallery> (live or freshly parsed) into product media order and returns the slide that was first
 * before the reorder (Horizon's selected-variant media) with its new index.
 * @param {Element | null} gallery
 * @param {string[]} order
 */
function reorderGallery(gallery, order) {
  const slideshow = gallery?.querySelector('slideshow-component');
  if (!gallery || !slideshow) return null;
  const slides = [...slideshow.querySelectorAll(':scope > slideshow-container > slideshow-slides > slideshow-slide')];
  if (slides.length < 2 || !order.length) return null;
  const perm = permutationFor(slides, order);
  const identity = perm.every((v, i) => v === i);
  if (!identity) {
    applyPermutation(slides, perm, SLOT_ATTRS.slide);
    for (const controls of slideshow.querySelectorAll('slideshow-controls')) {
      applyPermutation([...controls.querySelectorAll('.slideshow-controls__thumbnail')], perm, SLOT_ATTRS.thumbnail);
    }
    const zoom = gallery.querySelector('zoom-dialog');
    if (zoom) {
      applyPermutation([...zoom.querySelectorAll('.dialog-thumbnails-list__thumbnail')], perm, SLOT_ATTRS.zoomThumbnail);
      applyPermutation([...zoom.querySelectorAll('.dialog-zoomed-gallery > li')], perm, SLOT_ATTRS.zoomMedia);
    }
    applyPermutation([...gallery.querySelectorAll('.media-gallery__grid > li')], perm, SLOT_ATTRS.gridItem);
  }
  return { slideshow, slides: perm.map((i) => slides[i]), selectedIndex: perm.indexOf(0), changed: !identity };
}

class LcVariantPicker extends Component {
  /** @type {{ variants: Array<{ i: number, c: string | null, s: string | null, a: boolean, l: boolean }> }} */
  #data = { variants: [] };
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();
    const json = this.querySelector('script[type="application/json"]');
    try {
      this.#data = json?.textContent ? JSON.parse(json.textContent) : this.#data;
    } catch (error) {
      console.error('[lc-variant-picker] bad variant JSON', error);
    }
    this.addEventListener('change', this.#onChange);
    const selection = this.selection;
    this.#syncUi(selection);
    if (selection.size || selection.color) this.#announce(selection, null);
    // a colour applied by the block's "Color selected on load" (no ?variant) keeps the cover image first (Caroline 2026-09-24)
    const defaultColorOnly = this.dataset.defaultColor === 'true' && !selection.size;
    this.#initialGallery(Boolean(selection.size || (selection.color && !defaultColorOnly)));
  }

  /** The product's media ids in admin order (blocks/lc-variant-picker.liquid). */
  get #mediaOrder() {
    return (this.dataset.mediaOrder ?? '').split(',').filter(Boolean);
  }

  /**
   * Reorders a gallery and marks the slide to show. `selectedFirst` = the markup came from Horizon with the selected
   * variant's media as slide 0 (a `?variant=` load or a fetched section); with nothing selected the first product image shows.
   * @param {Element | null} gallery
   * @param {boolean} selectedFirst
   */
  #prepareGallery(gallery, selectedFirst) {
    const result = reorderGallery(gallery, this.#mediaOrder);
    if (!result || !gallery) return null;
    const index = selectedFirst ? result.selectedIndex : 0;
    // content-visibility hides every slide but the aria-hidden="false" one until the IntersectionObserver has run
    result.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== index)));
    // Horizon rendered initial-slide="0" for its own order; after the reorder the slideshow must start on `index`, or its
    // connect-time setup snaps to slide 0 (the cover) for a few frames before #showSlide corrects it (Caroline 2026-09-23:
    // "flashes the cover image first"). On a connected slideshow the attribute change re-selects through Horizon itself.
    result.slideshow.setAttribute('initial-slide', String(index));
    return { gallery, slideshow: result.slideshow, slide: result.slides[index], index };
  }

  /** @type {AbortController | undefined} */
  #showAbort;

  /**
   * Scrolls the (connected) gallery to the target slide: instantly before its first paint, then through Horizon's
   * `select()` once its own setup frame has run (scheduler order) so the counter, thumbnails and `slideshow:select` follow.
   * Chrome keeps a scroll-snap container snapped to the same element across DOM reorders (and re-snaps again when
   * Horizon's [in-viewport] toggles overflow), which drags the scroller back to the old first slide — so the position is
   * re-checked a few times; the first user swipe/select ends the checks.
   * @param {{ gallery: Element, slideshow: Element, slide: Element, index: number } | null} target
   */
  #showSlide(target) {
    if (!target) return;
    this.#showAbort?.abort();
    const abort = (this.#showAbort = new AbortController());
    if (!document.contains(target.gallery)) {
      // The fetched <media-gallery> is still in its parsed document: media-gallery.js swaps it in after this runs (its
      // `.then` settles later in the flush). Place the slide in the insertion's mutation microtask, before that frame
      // paints — the first painted frame is then the selected image, not the cover.
      const section = this.closest('.shopify-section') ?? document.body;
      const inserted = new MutationObserver(() => {
        if (!document.contains(target.gallery)) return;
        inserted.disconnect();
        this.#showSlide(target);
      });
      inserted.observe(section, { childList: true, subtree: true });
      abort.signal.addEventListener('abort', () => inserted.disconnect());
      return;
    }
    const { slideshow, slide, index } = target;
    const show = /** @type {any} */ (slideshow);
    const scroller = slideshow.querySelector(':scope > slideshow-container > slideshow-slides');
    const place = () => {
      if (!(scroller && slide instanceof HTMLElement)) return true;
      const misplaced = Math.abs(scroller.scrollLeft - slide.offsetLeft) > 1;
      // an explicit instant scroll: a plain scrollLeft assignment follows the scroller's smooth scroll-behavior and
      // animated from the cover to the selected image (Caroline 2026-09-23: "flashes the cover image first")
      if (misplaced) scroller.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
      return !misplaced;
    };
    place();
    // Horizon lays the slides out / enables scrolling only once its IntersectionObserver sets [in-viewport] on the
    // slideshow (a freshly swapped gallery starts without it, so the first place() finds nothing to scroll). Place again
    // in that attribute's mutation microtask — before the frame paints — so the first scrollable frame shows the slide.
    const viewport = new MutationObserver(() => {
      if (slideshow.hasAttribute('in-viewport')) place();
    });
    viewport.observe(slideshow, { attributes: true, attributeFilter: ['in-viewport'] });
    abort.signal.addEventListener('abort', () => viewport.disconnect());
    slideshow.addEventListener(
      'slideshow:select',
      (event) => {
        if (/** @type {any} */ (event).detail?.userInitiated) abort.abort();
      },
      { signal: abort.signal }
    );
    const sync = (attempt) => {
      if (abort.signal.aborted || !slideshow.isConnected) return;
      const placed = place();
      if (typeof show.select === 'function' && show.current !== index) show.select(index, undefined, { animate: false });
      if ((!placed || show.current !== index) && attempt < 3) setTimeout(() => sync(attempt + 1), 200 * (attempt + 1));
      else if (placed && show.current === index) abort.abort(); // settled: stop watching
    };
    scheduler.schedule(() => requestAnimationFrame(() => sync(0)));
  }

  /**
   * Initial page load: the server rendered the selected (or first available) variant's media first; put the live gallery
   * into product order, refresh the Components' refs (their MutationObserver only starts on idle) and show the right slide.
   * @param {boolean} preselected
   */
  #initialGallery(preselected) {
    const gallery = this.closest('.shopify-section')?.querySelector('media-gallery') ?? null;
    const target = this.#prepareGallery(gallery, preselected);
    if (!target) return;
    for (const component of [target.slideshow, gallery, gallery?.querySelector('zoom-dialog')]) {
      if (typeof (/** @type {any} */ (component))?.updatedCallback === 'function') /** @type {any} */ (component).updatedCallback();
    }
    this.#showSlide(target);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.#abort?.abort();
  }

  get hasColor() {
    return this.dataset.hasColor === 'true';
  }

  get hasSize() {
    return this.dataset.hasSize === 'true';
  }

  /** @returns {{ color: HTMLInputElement | null, size: HTMLInputElement | null }} */
  get selection() {
    return { color: this.#checked('color'), size: this.#checked('size') };
  }

  /** @param {'color' | 'size'} kind */
  #checked(kind) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`input[data-option="${kind}"]:checked`));
    return input && input.value !== '0' ? input : null;
  }

  /**
   * @param {HTMLInputElement | null} color
   * @param {HTMLInputElement | null} size
   */
  #variantFor(color, size) {
    if ((this.hasColor && !color) || (this.hasSize && !size)) return null;
    const colorName = color?.dataset.name;
    const sizeName = size?.dataset.name;
    return (
      this.#data.variants.find((v) => (!this.hasColor || v.c === colorName) && (!this.hasSize || v.s === sizeName)) ??
      null
    );
  }

  /**
   * Labels are refs (`swatches[]`, `sizes[]`); the data-testid attributes on them are for the tests only.
   * @param {{ color: HTMLInputElement | null, size: HTMLInputElement | null }} selection
   */
  #syncUi({ color }) {
    const { colorName } = this.refs;
    if (colorName instanceof HTMLElement) colorName.textContent = color?.dataset.name ?? '';

    for (const label of /** @type {HTMLLabelElement[]} */ (this.refs.swatches ?? [])) {
      const input = label.control;
      label.setAttribute('data-selected', String(Boolean(input && /** @type {HTMLInputElement} */ (input).checked)));
    }

    for (const label of /** @type {HTMLLabelElement[]} */ (this.refs.sizes ?? [])) {
      const input = /** @type {HTMLInputElement | null} */ (label.control);
      const name = input?.dataset.name;
      const available = this.#data.variants.some(
        (v) => v.a && (!this.hasSize || v.s === name) && (!color || v.c === color.dataset.name)
      );
      label.setAttribute('data-available', String(available));
      label.setAttribute('data-selected', String(Boolean(input?.checked)));
      const soldOut = label.querySelector('[data-soldout]');
      if (soldOut instanceof HTMLElement) soldOut.hidden = available;
    }
  }

  /**
   * Sync state event for lc-buy-button (and anything else in the section).
   * @param {{ color: HTMLInputElement | null, size: HTMLInputElement | null }} selection
   * @param {HTMLInputElement | null} source
   */
  #announce({ color, size }, source) {
    const variant = this.#variantFor(color, size);
    const full = (!this.hasColor || Boolean(color)) && (!this.hasSize || Boolean(size));
    const state = {
      full,
      colorName: color?.dataset.name ?? '',
      hex: color?.dataset.hex ?? '',
      mesh: color?.dataset.mesh === 'true',
      variantId: variant?.i ?? null,
      available: Boolean(variant?.a),
      low: Boolean(variant?.l),
      final: Boolean(variant?.f), // variant metafield custom.final_sale (Caroline 2026-09-23); the collection rule is product-wide
      source,
    };
    this.dispatchEvent(new CustomEvent('lc:selection', { bubbles: true, detail: state }));
    return { variant, full };
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.option) return;

    const selection = this.selection;
    const { color, size } = selection;
    this.#syncUi(selection);
    const { variant, full } = this.#announce(selection, input);

    const ids = [color?.value, size?.value].filter(Boolean);
    if (!ids.length) return;

    this.#abort?.abort();
    this.#abort = new AbortController();

    const base = (this.dataset.productUrl || '').split('?')[0];
    const url = `${base}?section_id=${this.dataset.sectionId}&option_values=${ids.join(',')}`;

    const selectedOptions = [];
    if (color) selectedOptions.push({ name: this.dataset.colorOption ?? '', value: color.dataset.name ?? '' });
    if (size) selectedOptions.push({ name: this.dataset.sizeOption ?? '', value: size.dataset.name ?? '' });

    const deferred = ProductSelectEvent.createPromise();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: { optionValueId: input.value, variantId: variant ? String(variant.i) : '', connectedProductUrl: '' },
        promise: deferred.promise,
      })
    );

    fetch(url, { signal: this.#abort.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`section render failed (${response.status})`);
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        this.#updatePrice(html);
        // product order before media-gallery.js adopts the markup; the section fetch rendered the selection's media first
        const target = this.#prepareGallery(html.querySelector('media-gallery'), true);
        const resource = full && variant ? { id: variant.i, available: variant.a } : null;
        deferred.resolve({
          variant: null,
          detail: { html, productId: this.dataset.productId ?? '', sourceId: input.value, resource },
        });
        // runs after media-gallery's own `.then` (registered at dispatch), i.e. once the new gallery is in the DOM
        if (target) deferred.promise.then(() => this.#showSlide(target)).catch(() => {});
      })
      .catch((error) => {
        deferred.reject(error);
        if (error?.name !== 'AbortError') console.error('[lc-variant-picker]', error);
      });

    const next = new URL(window.location.href);
    if (full && variant) next.searchParams.set('variant', String(variant.i));
    else next.searchParams.delete('variant');
    if (next.href !== window.location.href) {
      yieldToMainThread().then(() => history.replaceState({}, '', next.toString()));
    }
  };

  /** Swap lc-price contents from the re-rendered section (same idea as product-price.js). */
  #updatePrice(html) {
    const section = this.closest('.shopify-section');
    for (const el of section?.querySelectorAll('lc-price[data-block-id]') ?? []) {
      const fresh = html.querySelector(`lc-price[data-block-id="${/** @type {HTMLElement} */ (el).dataset.blockId}"]`);
      if (fresh) el.innerHTML = fresh.innerHTML;
    }
  }
}

if (!customElements.get('lc-variant-picker')) {
  customElements.define('lc-variant-picker', LcVariantPicker);
}
